import type { StageStory, StoryPanel } from "../campaign/types";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export class StoryPanels {
  private root: HTMLElement;
  private titleEl: HTMLElement;
  private scriptureEl: HTMLElement;
  private stageEl: HTMLElement;
  private artEl: HTMLElement;
  private captionEl: HTMLElement;
  private linesEl: HTMLElement;
  private progressEl: HTMLElement;
  private skipBtn: HTMLButtonElement;
  private nextBtn: HTMLButtonElement;
  private onComplete: (() => void) | null = null;
  private panels: StoryPanel[] = [];
  private index = 0;
  private mode: "intro" | "outro" = "intro";

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add("story-overlay");
    this.root.innerHTML = `
      <div class="story-shell" role="document">
        <div class="story-hero" data-stage>
          <div class="story-art" data-art aria-hidden="true"></div>
          <header class="story-header">
            <p class="story-kicker" data-mode>Story</p>
            <h2 data-title></h2>
            <p class="story-scripture" data-scripture></p>
          </header>
          <p class="story-caption" data-caption></p>
        </div>
        <div class="story-body">
          <div class="story-lines" data-lines></div>
        </div>
        <footer class="story-footer">
          <p class="story-progress" data-progress></p>
          <div class="story-actions">
            <button type="button" class="btn secondary" data-act="skip">Skip</button>
            <button type="button" class="btn primary" data-act="next">Continue</button>
          </div>
        </footer>
      </div>
    `;

    this.titleEl = this.root.querySelector("[data-title]")!;
    this.scriptureEl = this.root.querySelector("[data-scripture]")!;
    this.stageEl = this.root.querySelector("[data-stage]")!;
    this.artEl = this.root.querySelector("[data-art]")!;
    this.captionEl = this.root.querySelector("[data-caption]")!;
    this.linesEl = this.root.querySelector("[data-lines]")!;
    this.progressEl = this.root.querySelector("[data-progress]")!;
    this.skipBtn = this.root.querySelector("[data-act='skip']")!;
    this.nextBtn = this.root.querySelector("[data-act='next']")!;

    this.root.addEventListener("click", (e) => {
      const t = e.target as HTMLElement;
      const act = t.closest("[data-act]") as HTMLElement | null;
      if (!act) return;
      if (act.dataset.act === "skip") this.finish();
      if (act.dataset.act === "next") this.advance();
    });

    this.root.addEventListener("keydown", (e) => {
      if (!this.isOpen()) return;
      if (e.key === "Escape") {
        e.preventDefault();
        if (!this.skipBtn.disabled) this.finish();
        return;
      }
      if (e.key === "Enter" || e.key === " " || e.code === "Space") {
        e.preventDefault();
        e.stopPropagation();
        this.advance();
      }
    });
  }

  isOpen(): boolean {
    return !this.root.classList.contains("hidden");
  }

  /**
   * Show story panels. Completes via Continue on last panel, Skip (if allowed), or Escape.
   */
  show(
    story: StageStory,
    opts: {
      mode?: "intro" | "outro";
      /** When false, Skip is hidden (first clear intro). Default true for outro / replays. */
      canSkip?: boolean;
      onComplete: () => void;
    },
  ): void {
    this.mode = opts.mode ?? "intro";
    this.onComplete = opts.onComplete;
    this.panels =
      this.mode === "outro"
        ? (story.outro?.length ? story.outro : [])
        : story.intro;
    this.index = 0;

    if (this.panels.length === 0) {
      this.hide();
      opts.onComplete();
      return;
    }

    const modeLabel = this.root.querySelector("[data-mode]");
    if (modeLabel) {
      modeLabel.textContent =
        this.mode === "outro" ? "After the battle" : "Before the battle";
    }

    this.titleEl.textContent = story.title;
    this.scriptureEl.textContent = story.scriptureRef;

    const canSkip = opts.canSkip !== false;
    this.skipBtn.classList.toggle("hidden", !canSkip);
    this.skipBtn.disabled = !canSkip;

    this.root.classList.remove("hidden");
    document.body.classList.add("story-open");
    this.paint();
    this.nextBtn.focus();
  }

  hide(): void {
    this.root.classList.add("hidden");
    document.body.classList.remove("story-open");
    this.onComplete = null;
    this.panels = [];
  }

  private advance(): void {
    if (this.index >= this.panels.length - 1) {
      this.finish();
      return;
    }
    this.index += 1;
    this.paint();
  }

  private finish(): void {
    const cb = this.onComplete;
    this.hide();
    cb?.();
  }

  private paint(): void {
    const panel = this.panels[this.index];
    if (!panel) return;

    const n = this.panels.length;
    this.progressEl.textContent = `${this.index + 1} / ${n}`;
    this.nextBtn.textContent =
      this.index >= n - 1
        ? this.mode === "outro"
          ? "Continue"
          : "Begin battle"
        : "Continue";

    this.captionEl.textContent = panel.caption ?? "";
    this.captionEl.classList.toggle("hidden", !panel.caption);

    this.linesEl.innerHTML = panel.lines
      .map((line) => `<p>${escapeHtml(line)}</p>`)
      .join("");

    this.artEl.replaceChildren();
    if (panel.artSrc) {
      const img = document.createElement("img");
      img.src = panel.artSrc.startsWith("/")
        ? panel.artSrc
        : `/art/story/${panel.artSrc}`;
      img.alt = panel.caption ?? "";
      img.loading = "eager";
      img.onerror = () => {
        this.artEl.classList.add("is-placeholder");
        this.artEl.textContent = panel.caption || "Rebuild the Wall";
      };
      img.onload = () => {
        this.artEl.classList.remove("is-placeholder");
      };
      this.artEl.classList.remove("is-placeholder");
      this.artEl.appendChild(img);
    } else {
      this.artEl.classList.add("is-placeholder");
      this.artEl.textContent = panel.caption || this.titleEl.textContent || "";
    }

    this.stageEl.classList.remove("story-stage-in");
    // Force reflow for a light re-entry animation
    void this.stageEl.offsetWidth;
    this.stageEl.classList.add("story-stage-in");
  }
}
